import { Cidade } from '../../cidade/cidade';
import { Estado } from '../../estado/estado';
import { RegiaoGeograficaImediata } from '../../regiao-geografica/imediata/regiao-geografica-imediata';
import { RegiaoGeograficaIntermediaria } from '../../regiao-geografica/intermediaria/regiao-geografica-intermediaria';

export class RegiaoLocalizacoesResponse {
    readonly estados: RegiaoLocalizacoesEstadoResponse[];
    readonly regioesGeograficasIntermediarias: RegiaoLocalizacoesRegiaoGeograficaIntermediariaResponse[];
    readonly regioesGeograficasImediatas: RegiaoLocalizacoesRegiaoGeograficaImediataResponse[];
    readonly cidades: RegiaoLocalizacoesCidadeResponse[];

    constructor(props: Partial<RegiaoLocalizacoesResponse>) {
        Object.assign(this, props);
    }

    /**
     * Cria o objeto através do JSON informado
     *
     * @param json JSON com as propriedades para criar o objeto
     * @returns objet criado
     */
    static fromJSON(json: string): RegiaoLocalizacoesResponse {
        return new RegiaoLocalizacoesResponse(JSON.parse(json));
    }

    /**
     * Retorna os estados
     *
     * @returns Array de estados
     */
    getEstados(): Estado[] {
        // Se não houver estados, retorna array vazio
        if (!this.estados) {
            return [];
        }

        const estados: Estado[] = [];
        this.estados?.forEach(resEstado => {
            estados.push(new Estado({
                id: resEstado.id,
                uf: resEstado.uf,
                nome: resEstado.nome,
                codigoIbge: resEstado.codigoIbge
            }));
        });
        return estados;
    }

    /**
     * Retorna todas as regiões geográficas intermediárias ou filtra por idEstado se fornecido
     *
     * @param idEstado ID opcional do estado para filtrar as regiões
     * @returns Array de regiões geográficas intermediárias filtradas ou todas as regiões se idEstado não for fornecido
     */
    getRegioesGeograficasIntermediarias(idEstado?: number): RegiaoGeograficaIntermediaria[] {
        // Se não houver regiões intermediárias, retorna array vazio
        if (!this.regioesGeograficasIntermediarias) {
            return [];
        }

        const regioes: RegiaoGeograficaIntermediaria[] = [];
        for (let i = 0; i < this.regioesGeograficasIntermediarias.length; i++) {
            const resRegiao = this.regioesGeograficasIntermediarias[i];

            // Se idEstado foi fornecido e não corresponde, ignora
            if (idEstado && resRegiao.idEstado !== idEstado) {
                continue;
            }

            // Se todos os fitros forem atendidos, então criamos a região
            regioes.push(new RegiaoGeograficaIntermediaria({
                id: resRegiao.id,
                idEstado: resRegiao.idEstado,
                codigoIbge: resRegiao.codigoIbge,
                nome: resRegiao.nome
            }));
        }

        return regioes;
    }

    /**
     * Retorna regiões geográficas imediatas com opção de filtrar por idEstado e/ou idRegiaoGeograficaIntermediaria
     *
     * @param idEstado ID opcional do estado para filtrar as regiões
     * @param idRegiaoGeograficaIntermediaria ID opcional da região intermediária para filtrar as regiões
     * @returns Array de regiões geográficas imediatas filtradas ou todas as regiões se nenhum parâmetro for fornecido
     */
    getRegioesGeograficasImediatas(idEstado?: number, idRegiaoGeograficaIntermediaria?: number): RegiaoGeograficaImediata[] {
        // Se não houver regiões imediatas, retorna array vazio
        if (!this.regioesGeograficasImediatas) {
            return [];
        }

        const regioes: RegiaoGeograficaImediata[] = [];
        for (let i = 0; i < this.regioesGeograficasImediatas.length; i++) {
            const resRegiao = this.regioesGeograficasImediatas[i];

            // Se idEstado foi fornecido e não corresponde, ignora
            if (idEstado && resRegiao.idEstado !== idEstado) {
                continue;
            }

            // Se idRegiaoGeograficaIntermediaria foi fornecido e não corresponde, ignora
            if (idRegiaoGeograficaIntermediaria && resRegiao.idRegiaoGeograficaIntermediaria !== idRegiaoGeograficaIntermediaria) {
                continue
            }

            // Se todos os fitros forem atendidos, então criamos a região
            regioes.push(new RegiaoGeograficaImediata({
                id: resRegiao.id,
                idRegiaoGeograficaIntermediaria: resRegiao.idRegiaoGeograficaIntermediaria,
                codigoIbge: resRegiao.codigoIbge,
                nome: resRegiao.nome
            }));
        }

        return regioes;
    }

    /**
     * Retorna cidades com opção de filtrar por idEstado, idRegiaoGeograficaIntermediaria, idRegiaoGeograficaImediata
     *
     * @param idEstado ID opcional do estado para filtrar as cidades
     * @param idRegiaoGeograficaIntermediaria ID opcional da região intermediária para filtrar as cidades
     * @param idRegiaoGeograficaImediata ID opcional da região imediata para filtrar as cidades
     * @returns Array de cidades filtradas ou todas as cidades se nenhum parâmetro for fornecido
     */
    getCidades(idEstado?: number, idRegiaoGeograficaIntermediaria?: number, idRegiaoGeograficaImediata?: number, nome?: string): Cidade[] {
        // Se não houver cidades, retorna array vazio
        if (!this.cidades) {
            return [];
        }

        // Normaliza o nome da cidade (remove acentos, converte para minúsculas)
        const normalizarNome = (texto: string): string => texto
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');

        const nomeNormalizado = nome ? normalizarNome(nome) : undefined;


        const cidades: Cidade[] = [];
        for (let i = 0; i < this.cidades.length; i++) {
            const resCidade = this.cidades[i];

            // Se idEstado foi fornecido e não corresponde, ignora
            if (idEstado && resCidade.idEstado !== idEstado) {
                continue;
            }

            // Se idRegiaoGeograficaIntermediaria foi fornecido e não corresponde, ignora
            if (idRegiaoGeograficaIntermediaria && resCidade.idRegiaoGeograficaIntermediaria !== idRegiaoGeograficaIntermediaria) {
                continue;
            }

            // Se idRegiaoGeograficaImediata foi fornecido e não corresponde, continue;
            if (idRegiaoGeograficaImediata && resCidade.idRegiaoGeograficaImediata !== idRegiaoGeograficaImediata) {
                continue;
            }

            // Se nome foi fornecido e o nome da cidade não contém a string fornecida, ignora
            if (nome && resCidade.nome) {
                const nomeNormalizadoCidade = normalizarNome(resCidade.nome);
                if (!nomeNormalizadoCidade.includes(nomeNormalizado!)) {
                    continue;
                }
            }

            // Se todos os fitros forem atendidos, então criamos a cidade
            cidades.push(new Cidade({
                id: resCidade.id,
                codigoIbge: resCidade.codigoIbge,
                nome: resCidade.nome,
                idEstado: resCidade.idEstado,
                estado: new Estado({
                    id: resCidade.idEstado,
                    uf: resCidade.ufEstado
                }),
                idRegiaoGeograficaImediata: resCidade.idRegiaoGeograficaImediata
            }));
        }

        return cidades;
    }
}


export class RegiaoLocalizacoesEstadoResponse {
    id: number;
    codigoIbge: number;
    uf: string;
    nome: string;
}

export class RegiaoLocalizacoesRegiaoGeograficaIntermediariaResponse {
    id: number;
    codigoIbge: number;
    nome: string;
    idEstado: number;
}

export class RegiaoLocalizacoesRegiaoGeograficaImediataResponse {
    id: number;
    codigoIbge: number;
    nome: string;
    idEstado: number;
    idRegiaoGeograficaIntermediaria: number;
}

export class RegiaoLocalizacoesCidadeResponse {
    id: number;
    codigoIbge: number;
    nome: string;
    idEstado: number;
    ufEstado: string;
    idRegiaoGeograficaIntermediaria: number;
    idRegiaoGeograficaImediata: number;
}
